import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logAuditEvent } from '@/hooks/useAuditLog';

export interface SeoSetting {
  id: string;
  page_path: string;
  title: string | null;
  description: string | null;
  keywords: string | null;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  created_at: string;
  updated_at: string;
}

export const useSeoSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: seoSettings, isLoading } = useQuery({
    queryKey: ['seo-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_settings')
        .select('*')
        .order('page_path');
      
      if (error) throw error;
      return data as SeoSetting[];
    },
  });

  const createSeoSetting = useMutation({
    mutationFn: async (setting: Omit<SeoSetting, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('seo_settings')
        .insert(setting)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['seo-settings'] });
      logAuditEvent({ action: 'seo_setting_created' as any, resourceType: 'seo_settings' as any, resourceId: data.id, details: { page_path: data.page_path } });
      toast({ title: 'SEO setting created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create SEO setting', description: error.message, variant: 'destructive' });
    },
  });

  const updateSeoSetting = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SeoSetting> & { id: string }) => {
      const { data, error } = await supabase
        .from('seo_settings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['seo-settings'] });
      logAuditEvent({ action: 'seo_setting_updated' as any, resourceType: 'seo_settings' as any, resourceId: data.id, details: { page_path: data.page_path } });
      toast({ title: 'SEO setting updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update SEO setting', description: error.message, variant: 'destructive' });
    },
  });

  const deleteSeoSetting = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('seo_settings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['seo-settings'] });
      logAuditEvent({ action: 'seo_setting_deleted' as any, resourceType: 'seo_settings' as any, resourceId: id });
      toast({ title: 'SEO setting deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to delete SEO setting', description: error.message, variant: 'destructive' });
    },
  });

  return {
    seoSettings,
    isLoading,
    createSeoSetting,
    updateSeoSetting,
    deleteSeoSetting,
  };
};

export const useSeoSettingByPath = (pagePath: string) => {
  return useQuery({
    queryKey: ['seo-settings', pagePath],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('seo_settings')
        .select('*')
        .eq('page_path', pagePath)
        .maybeSingle();
      
      if (error) throw error;
      return data as SeoSetting | null;
    },
  });
};
